import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { postData } from '../services/api';  // Assuming you have a `postData` function to make API calls
import apiUrls from '../config/apiUrls';
import handleApiResponse from '../utils/handleApiResponse';
import IApiResponse from '../types/IApiResponse';
import { useLoading } from './LoadingContext';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

interface AuthContextType {
    isAuthenticated: boolean;
    setIsAuthenticated: (value: boolean) => void;
    login: (username: string, password: string) => Promise<{ success: boolean, response?: any }>;
    logout: () => void;
    signup: (email: string, password: string) => Promise<{ success: boolean, response?: any }>;
    userData: any; // To hold the stored 'data' field
    allowPresentaion : boolean
    setAllowPresentaion : (value : boolean) => void;
    signupSuccess : boolean
    setIsSignUpSuccess: (value : boolean) => void;
}

interface AuthProviderProps {
    children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const maxSessionDuration = 120 * 60 * 1000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {

    const {startLoading , stopLoading} = useLoading();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userData, setUserData] = useState<any>(null);  // To store 'data' field from response
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const timeoutId = useRef<NodeJS.Timeout | null>(null);
    const intervalId = useRef<NodeJS.Timeout | null>(null);
    const [signupSuccess , setIsSignUpSuccess] = useState<boolean>(false)
    

    const [allowPresentaion , setAllowPresentaion] = useState<boolean>(false)

    const resetTimeout = useCallback(() => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }
        timeoutId.current = setTimeout(() => {
            logout();
        }, maxSessionDuration); // 120 minutes in milliseconds
    }, []);

    const getDeviceId = async () => {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        //console.log("device id => ", result.visitorId); // This is the unique identifier
        return result.visitorId;
    };

    const login = async (username: string, password: string): Promise<{ success: boolean, response?: any }> => {
        try {
            var deviceId = await getDeviceId();
            const requestParameters = {
                username: username,
                password: password,
                footprint: deviceId,
              };
            const response: IApiResponse<any> = await postData(`${apiUrls["Login"]}`, 
                // { username, password },
                requestParameters,
                true);

            const { data } = response;
            if (data) {
                localStorage.setItem('userData', JSON.stringify(data.id));  // Save the 'data' field in localStorage
                localStorage.setItem('allowPresentation', JSON.stringify(data.allowPresentation));
                localStorage.setItem('SessionKey',data.sessionKey)
                localStorage.setItem('uName',username)
                setUserData(data.id);  // Set 'data' in the state
                setIsAuthenticated(true);
                setAllowPresentaion(data.allowPresentation)
                resetTimeout();
                return { success: true, response: response };
            } else {
                setIsAuthenticated(false);
                return { success: false, response: response };
            }
        } catch (error) {
            console.error('Login failed:', error);
            return { success: false, response: error };
        }
    };

    const signup = async (email: string, password: string): Promise<{ success: boolean, response?: any }> => {
        try {
          const requestParameters = {
            userName: email,
            password: password,
          };
      
          const response: IApiResponse<any> = await postData(apiUrls["Register"], requestParameters);
        //   const { data } = response;
      
          if (response?.isSuccess) { // adjust condition based on your actual API
            return { success: true, response: response };
          } else {
            return { success: false, response: response };
          }
        } catch (error) {
          console.error('Sign up failed:', error);
          return { success: false, response: error };
        }
      };
      
      

    const logout = () => {
        localStorage.removeItem('userData');  // Clear the stored data on logout
        localStorage.removeItem('sessionStartTime');  // Clear the stored data on logout
        localStorage.removeItem('allowPresentation')
        localStorage.clear()
        // localStorage.removeItem('SessionKey')
        setIsAuthenticated(false);
        setUserData(null);
        clearTimeout(timeoutId.current!);
        if (intervalId.current) {
            clearInterval(intervalId.current); // Clear the interval when logging out
        }
        window.location.href = '/login';
    };

    const checkSessionValidity = useCallback(() => {
        const sessionStartTime = localStorage.getItem('sessionStartTime');
        if (sessionStartTime) {
            const sessionStart = new Date(sessionStartTime).getTime();
            const isValid = Date.now() - sessionStart < maxSessionDuration;
            if (isValid) {
                resetTimeout();
                return true;
            }
        }
        logout();
        setIsSessionExpired(true); 
        return false;
    }, [logout,resetTimeout]);

    useEffect(() => {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
            const parsedData = JSON.parse(storedUserData);
            setUserData(parsedData);  // Retrieve and set the data from localStorage
            // setAllowPresentaion(presentation)
            setIsAuthenticated(true);
        }

        intervalId.current = setInterval(() => {
             checkSessionValidity();
        }, 120 * 60 * 1000); 

        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current); // Clear interval when unmounted
            }
            if (timeoutId.current) {
                clearTimeout(timeoutId.current); // Clear timeout when unmounted
            }
        };

        // const events = ['click', 'mousemove', 'keypress', 'scroll', 'keydown'];

        // const handleUserActivity = () => {
        //     if (isSessionExpired) {
        //         window.location.href = '/login';
        //     }
        // };

        // events.forEach(event => window.addEventListener(event, handleUserActivity));

        // return () => {
        //     events.forEach(event => window.removeEventListener(event, handleUserActivity));
        //     clearTimeout(timeoutId.current!);
        // };
    }, [checkSessionValidity, resetTimeout]);

    return (
        <AuthContext.Provider value={{ isAuthenticated,setIsAuthenticated, login, logout, signup, userData , allowPresentaion , setAllowPresentaion ,signupSuccess, setIsSignUpSuccess}}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
